<script>
  /*____________ Config  ____________*/
  export let tour;
  
  /*____________ Imports ____________*/
  import { onMount } from "svelte";
  import i18n from "./i18n/i18n";
  import { tourFunctions } from "../tour-functions";
  const { convertColor } = tourFunctions;
  import { fixFontFamily } from "./utils"
  /* ____________ Mobile Detection ____________ */
  import UA from "ua-parser-js";
  const ua = new UA();
  let isMobile = ua.getResult().device["type"] == "mobile";

  /*____________ Components ____________*/
  import Krpano from "./Krpano.svelte";
  
  // Add custom prop
  export let custom = false;
  
  // Get embed ID from tour config
  $: embedId = tour.embedId;
  
  // Font customization options with defaults
  $: embedIntroTextFontSize = tour.embedIntroTextFontSize || 28;
  $: embedButtonFontSize = tour.embedButtonFontSize || 24;
  $: embedButtonFont = fixFontFamily(tour.embedButtonFont || 'var(--font-heading), sans-serif');
  $: embedIntroTextFont = fixFontFamily(tour.embedIntroTextFont || 'var(--font-body), sans-serif');
  
  onMount(() => {
    // TourEmbed component mounted
  });

  // Function to handle embed expansion
  const handleEmbedExpand = () => {
    if (embedId && window.parent !== window) {
      // Send message to parent window to expand the embed
      window.parent.postMessage({
        type: 'covecreek:expand',
        embedId: embedId
      }, '*');
    }
  };

  const { theme, form } = tour;
  const sceneButton = tour.sceneButton;
  const colors = {
    themeColor: convertColor(theme?.primaryColor || "#000000"),
    controlsTextColor: convertColor(theme?.controlsTextColor || "#FFFFFF"),
    loaderColor: convertColor(theme?.loaderColor || "#FFFFFF"),
    loaderBg: convertColor(theme?.loaderBgColor || "#000000"),
    hotspotHoverColor: convertColor(theme?.hotspotColor || "#000000"),
    calloutColor: convertColor(
      theme?.calloutColor || theme?.primaryColor || "#000000",
    ),
    hotspotColor: convertColor(theme?.hotspotColor || "#000000"),
    hotspotTextColor: convertColor(theme?.hotspotTextColor || "#FFFFFF"),
    popupTextColor: convertColor(theme?.popupTextColor || "#FFFFFF"),
    popupBgColor: convertColor(
      theme?.popupBgColor || theme?.primaryColor || "#FFFFFF",
    ),
    sceneButtonBgColor: convertColor(
      sceneButton?.bgColor || theme?.primaryColor || "#000000",
    ),
    sceneButtonTextColor: convertColor(sceneButton?.textColor || "#FFFFFF"),
    formPopupTextColor: convertColor(
      form?.popupTextColor || theme?.popupTextColor || "#FFFFFF",
    ),
    formPopupBgColor: convertColor(
      form?.popupBgColor ||
        theme?.popupBgColor ||
        theme?.primaryColor ||
        "#000000",
    ),
  };
  tourFunctions.setupColors(colors);
  const fonts = {
    fontHeading: tour.fontHeading || "Oswald",
    fontBody: tour.fontBody || "Raleway",
  };
  tourFunctions.setupFonts(fonts);
  import { Preloader } from "./preloader";
  const preloader = new Preloader(tour);
  
  const logoLoading =
    (isMobile && tour.logoLoadingMobile?.url
      ? tour.logoLoadingMobile
      : tour.logoLoading) || tourLogo;
</script>

<!-- /*____________ Markup ____________*/ -->
<div class="embed">
  <div class="overlay">
    {#if !custom}
      <div class="embed-intro">
        <h1 style="font-size: {embedIntroTextFontSize}px; font-family: {embedIntroTextFont}">
          {tour.embedIntroText || $i18n.t('tourEmbed.intro') + ' ' + tour.name}
        </h1>
        <button 
          style="font-size: {embedButtonFontSize}px; font-family: {embedButtonFont}"
          on:click={handleEmbedExpand}
          on:keydown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleEmbedExpand();
            }
          }}
          tabindex="0"
        >
          {tour.embedButtonText || $i18n.t('tourEmbed.embedButton')}
        </button>
      </div>
    {/if}
    <slot />
  </div>
  <Krpano 
    config={tour}
    {colors}
    loaderEnabled={false}
    logoLoading={logoLoading.url}
    logoLoadingWidth={logoLoading.width}
    enableTracking={false}
  />
</div>

<style>
.embed {
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 999;
  display: grid;
  grid-template-rows: 1fr 1fr 1fr 1fr;
  justify-items: center; 
  align-items: end;
}

.embed-intro {
  grid-row: 3 / 4;
  text-align: center;
}

h1 {
  font-family: var(--font-body), sans-serif;
  font-size: 28px;
  margin: 6px;
  font-weight: 700;
  color: #fff;
  text-shadow: 5px 5px 10px rgba(0, 0, 0, 0.5);
}

button {
  appearance: none;
  margin: 0;
  cursor: pointer;

  border: 3px solid transparent;
  border-radius: 3px;
  padding: 8px;

  font-size: 24px;
  font-family: var(--font-heading), sans-serif;
  font-weight: 700;
  color: var(--controls-text-color);
  background: rgba(var(--theme-color-rgb), 0.8);

  text-align: center;
  box-sizing: border-box;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  line-height: 1;
  border: 2px solid transparent;

  text-decoration: none;
}
</style>
